/**
 * Pakete-Widget P1: Ansicht „Website-Kategorien“ auf der Paketliste (Mockup „Pakete als Widget“,
 * freigegeben 04.10.2026). Kategorien bestimmen, auf welcher Website-Seite ein Paket steht; die
 * Reihenfolge hier gilt auch auf der Website. API: /api/package-categories.
 *
 * Die Karte „Stand der Website“ mit „Website jetzt aktualisieren“ kommt mit P3.
 */
import { useState, type FormEvent } from "react";
import { apiMutate } from "../hooks/useApi";
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Icon,
  Input,
  Modal,
  useBestaetigung,
} from "./components";

type Sprache = "en" | "it" | "fr";
const SPRACHEN: { key: Sprache; label: string }[] = [
  { key: "en", label: "English" },
  { key: "it", label: "Italiano" },
  { key: "fr", label: "Français" },
];

export interface PaketKategorie {
  id: string;
  name: string;
  slug: string;
  translations?: Partial<Record<Sprache, { name?: string }>> | null;
  sortOrder: number;
  archivedAt?: string | null;
  packageCount?: number;
}

function gepflegt(k: PaketKategorie): Sprache[] {
  return SPRACHEN.map((s) => s.key).filter(
    (l) => (k.translations?.[l]?.name ?? "").trim() !== "",
  );
}

export function WebsiteKategorienKarte({
  kategorien,
  neuLaden,
}: {
  kategorien: PaketKategorie[];
  neuLaden: () => void;
}) {
  const { bestaetigen, dialog } = useBestaetigung();
  const [bearbeiten, setBearbeiten] = useState<PaketKategorie | "neu" | null>(
    null,
  );
  const [fehler, setFehler] = useState<string | null>(null);
  const [gezogen, setGezogen] = useState<string | null>(null);

  async function aufrufen(fn: () => Promise<unknown>) {
    setFehler(null);
    try {
      await fn();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      neuLaden();
    }
  }

  function verschieben(id: string, nach: number) {
    const ids = kategorien.map((k) => k.id).filter((x) => x !== id);
    ids.splice(Math.max(0, Math.min(nach, ids.length)), 0, id);
    void aufrufen(() =>
      apiMutate("/api/package-categories/order", "PUT", { ids }),
    );
  }

  async function loeschen(k: PaketKategorie) {
    const ok = await bestaetigen({
      titel: `Kategorie „${k.name}“ löschen?`,
      text: k.packageCount
        ? `${k.packageCount} Paket${k.packageCount === 1 ? "" : "e"} verlieren diese Zuordnung. Ein Paket ohne weitere Kategorie verschwindet von der Website.`
        : "Die Kategorie ist keinem Paket zugeordnet.",
      bestaetigen: "Löschen",
      gefahr: true,
    });
    if (ok)
      void aufrufen(() =>
        apiMutate(`/api/package-categories/${k.id}`, "DELETE"),
      );
  }

  return (
    <>
      {dialog}
      <Card className="overflow-hidden" data-testid="website-kategorien">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-outline-variant/30 px-5 py-4 dark:border-outline/30">
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Website-Kategorien · bestimmen, wo ein Paket auf der Website steht
          </p>
          <Button
            type="button"
            variant="primary"
            onClick={() => setBearbeiten("neu")}
          >
            <Icon name="add" className="text-[18px]" /> Kategorie anlegen
          </Button>
        </div>
        {fehler && <ErrorNote message={fehler} />}
        {kategorien.length === 0 ? (
          <EmptyState
            icon="sell"
            title="Noch keine Website-Kategorien"
            hint="Lege z. B. „Weihnachtsfeier“ oder „Firmenfeier“ an und ordne die Pakete zu."
          />
        ) : (
          <ul>
            {kategorien.map((k, i) => {
              const sprachen = gepflegt(k);
              return (
                <li
                  key={k.id}
                  draggable
                  onDragStart={() => setGezogen(k.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (gezogen && gezogen !== k.id) verschieben(gezogen, i);
                    setGezogen(null);
                  }}
                  className={`flex items-center gap-3 border-b border-outline-variant/20 px-5 py-3 last:border-b-0 dark:border-outline/20 ${k.archivedAt ? "opacity-60" : ""}`}
                >
                  <Icon
                    name="drag_indicator"
                    className="cursor-grab text-[20px] text-secondary"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-body-md font-semibold">
                      {k.name}
                      {k.archivedAt && (
                        <span className="ml-2 rounded-full bg-surface-container-high px-2 py-0.5 text-[11px] font-normal dark:bg-white/[0.08]">
                          archiviert
                        </span>
                      )}
                    </p>
                    <p className="font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                      Slug: {k.slug} ·{" "}
                      {sprachen.length === 3
                        ? "EN/IT/FR gepflegt"
                        : `${sprachen.length ? sprachen.map((l) => l.toUpperCase()).join("/") : "keine Sprache"} gepflegt`}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[11px] font-semibold dark:bg-white/[0.08]">
                    {k.packageCount ?? 0}{" "}
                    {(k.packageCount ?? 0) === 1 ? "Paket" : "Pakete"}
                  </span>
                  {/* Tastatur-Alternative zum Ziehen. */}
                  <button
                    type="button"
                    className="rounded p-1 text-secondary disabled:opacity-30"
                    aria-label={`${k.name} nach oben`}
                    disabled={i === 0}
                    onClick={() => verschieben(k.id, i - 1)}
                  >
                    <Icon name="arrow_upward" className="text-[18px]" />
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 text-secondary disabled:opacity-30"
                    aria-label={`${k.name} nach unten`}
                    disabled={i === kategorien.length - 1}
                    onClick={() => verschieben(k.id, i + 1)}
                  >
                    <Icon name="arrow_downward" className="text-[18px]" />
                  </button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="!py-1"
                    onClick={() => setBearbeiten(k)}
                  >
                    Bearbeiten
                  </Button>
                  <button
                    type="button"
                    className="rounded-lg p-2 text-secondary hover:bg-surface-container-high dark:hover:bg-surface-variant/30"
                    title={k.archivedAt ? "Wiederherstellen" : "Archivieren"}
                    aria-label={
                      k.archivedAt
                        ? `${k.name} wiederherstellen`
                        : `${k.name} archivieren`
                    }
                    onClick={() =>
                      void aufrufen(() =>
                        apiMutate(`/api/package-categories/${k.id}`, "PATCH", {
                          archived: !k.archivedAt,
                        }),
                      )
                    }
                  >
                    <Icon name={k.archivedAt ? "toggle_off" : "toggle_on"} />
                  </button>
                  <button
                    type="button"
                    className="rounded-lg p-2 text-error hover:bg-error/10"
                    title="Löschen"
                    aria-label={`${k.name} löschen`}
                    onClick={() => void loeschen(k)}
                  >
                    <Icon name="delete" className="text-[18px]" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <p className="flex items-center gap-2 border-t border-outline-variant/30 px-5 py-3 font-body-sm text-[12px] text-secondary dark:border-outline/30 dark:text-secondary-fixed-dim">
          <Icon name="info" className="text-[16px]" /> Reihenfolge per Ziehen
          ändern. Sie gilt auch auf der Website.
        </p>
      </Card>
      <KategorieDialog
        key={bearbeiten === "neu" ? "neu" : (bearbeiten?.id ?? "zu")}
        kategorie={bearbeiten}
        onClose={() => setBearbeiten(null)}
        onGespeichert={() => {
          setBearbeiten(null);
          neuLaden();
        }}
      />
    </>
  );
}

function KategorieDialog({
  kategorie,
  onClose,
  onGespeichert,
}: {
  kategorie: PaketKategorie | "neu" | null;
  onClose: () => void;
  onGespeichert: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const k = kategorie === "neu" ? null : kategorie;

  async function speichern(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const translations = Object.fromEntries(
      SPRACHEN.map(({ key }) => [
        key,
        { name: String(form.get(key) ?? "").trim() },
      ]),
    );
    setBusy(true);
    setFehler(null);
    try {
      const body = {
        name: String(form.get("name") ?? "").trim(),
        translations,
      };
      if (k) await apiMutate(`/api/package-categories/${k.id}`, "PATCH", body);
      else await apiMutate("/api/package-categories", "POST", body);
      onGespeichert();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={kategorie != null}
      onClose={onClose}
      width="480px"
      title={k ? "Kategorie bearbeiten" : "Kategorie anlegen"}
      subtitle={
        k
          ? `Slug: ${k.slug}`
          : "Der Slug für die Website entsteht aus dem Namen."
      }
      onSubmit={(e) => void speichern(e)}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Speichert …" : "Speichern"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {fehler && <ErrorNote message={fehler} />}
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold">
            Name (Deutsch) *
          </span>
          <Input
            name="name"
            required
            defaultValue={k?.name ?? ""}
            placeholder="z. B. Weihnachtsfeier"
          />
        </label>
        {SPRACHEN.map(({ key, label }) => (
          <label key={key} className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold">
              Name ({label})
            </span>
            <Input
              name={key}
              defaultValue={k?.translations?.[key]?.name ?? ""}
            />
          </label>
        ))}
      </div>
    </Modal>
  );
}
